import { useEffect, useMemo, useRef, useState } from "react";
import { ShieldAlert, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Checkbox,
  ToggleGroup,
  ToggleGroupItem,
  Input,
} from "@capacit-com/lara-law-shared-ui";
import {
  numberSpans,
  placeholderFor,
  PII_TIERS,
  type DecidedSpan,
  type PiiSpan,
  type PiiType,
  type SpanDecision,
} from "@/lib/pii";
import {
  PII_TYPE_BADGE_CLASS,
  PII_TYPE_DOT_CLASS,
  PII_TYPE_LABELS,
} from "./piiTypeMeta";

/**
 * Chat PII review dialog (LAR-27 v2). The in-context highlighted prompt IS the
 * control surface — no per-finding list. Each highlight shows its entity number
 * and a corner marker: green ✓ = will be anonymised (default), red ✗ = kept
 * (override). Clicking toggles it; highlights that share an `entityId` are one
 * entity and toggle together. "Vis anonymiseret" previews the outgoing redacted
 * text. The user can also select any un-highlighted text and classify it as PII
 * — the backstop for anything the NER model misses (plan Q1).
 *
 * Keeping a hard-tier identifier is a plain toggle like everything else (the
 * in-dialog confirm was dropped after team testing — tenant training owns that
 * behaviour); hard overrides are still always audited server-side.
 */

// Danish UI strings. TODO(LAR-27): migrate to the useContent/i18n content pack.
const TEXT = {
  title: "Identificerbare oplysninger fundet",
  description:
    "Vi fandt identificerbare oplysninger i din besked. De anonymiseres, før beskeden sendes. Klik på en fremhævning for at beholde den, eller marker selv tekst, der skal anonymiseres.",
  showAnonymised: "Vis anonymiseret",
  selectHint: "Marker tekst for at tilføje",
  willAnonymise: "Anonymiseres",
  willKeep: "Beholdes",
  markAs: (t: string) => `Marker “${t}” som:`,
  typeName: "Navn",
  typeAddress: "Adresse",
  typeOther: "Andet",
  otherLabel: "Etiket (fx diagnose)",
  add: "Tilføj",
  cancelManual: "Annuller",
  cancel: "Annuller",
  send: "Send",
  summary: (found: number, kept: number) =>
    `${found} fundet · ${kept} beholdes`,
} as const;

/** Types the user can pick when manually classifying a selection. */
const MANUAL_TYPES: ReadonlyArray<{ value: PiiType; label: string }> = [
  { value: "name", label: TEXT.typeName },
  { value: "address", label: TEXT.typeAddress },
  { value: "custom", label: TEXT.typeOther },
];

type PiiReviewDialogProps = {
  open: boolean;
  originalText: string;
  spans: PiiSpan[];
  onCancel: () => void;
  onConfirm: (decided: DecidedSpan[]) => void;
};

interface ManualDraft {
  start: number;
  end: number;
  text: string;
}

/** One decision per entity (type + entityId), so coreferent mentions toggle together. */
const groupKeyOf = (s: PiiSpan): string =>
  `${s.type}#${s.entityId ?? `${s.start}-${s.end}`}`;

/**
 * Absolute original-text offset of a selection endpoint, or null when the
 * endpoint is not in selectable prompt content.
 *
 * Each PLAIN (un-highlighted) segment of the preview carries its segment start
 * as `data-pii-offset`, so an endpoint maps to `segmentStart + offsetInNode` —
 * no walking, no dependence on what OTHER segments render. That keeps the math
 * exact in the anonymised view too, where highlights display `[NAVN1]`-style
 * placeholders whose lengths differ from the original. Endpoints inside
 * highlights or their decoration (numbers, ✓/✗ badges) have no
 * `data-pii-offset` ancestor and yield null — selections touching an existing
 * highlight are rejected by `overlapsExisting` anyway.
 */
function offsetOf(node: Node, offsetInNode: number): number | null {
  if (node.nodeType !== Node.TEXT_NODE) return null;
  const anchor = node.parentElement?.closest("[data-pii-offset]");
  if (!anchor) return null;
  const segmentStart = Number(anchor.getAttribute("data-pii-offset"));
  if (Number.isNaN(segmentStart)) return null;
  return segmentStart + offsetInNode;
}

/**
 * Map the current text selection to a trimmed `[start,end)` range in the
 * original prompt. Works in both the original and the anonymised view (offsets
 * are anchored per plain segment, see `offsetOf`).
 */
function selectionToRange(
  container: HTMLElement,
  originalText: string
): ManualDraft | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (
    !container.contains(range.startContainer) ||
    !container.contains(range.endContainer)
  ) {
    return null;
  }
  let start = offsetOf(range.startContainer, range.startOffset);
  let end = offsetOf(range.endContainer, range.endOffset);
  if (start === null || end === null) return null;
  if (start > end) [start, end] = [end, start];
  // Trim surrounding whitespace so a sloppy drag still yields a tidy span.
  while (start < end && /\s/.test(originalText[start])) start++;
  while (end > start && /\s/.test(originalText[end - 1])) end--;
  if (start >= end) return null;
  return { start, end, text: originalText.slice(start, end) };
}

const overlapsExisting = (draft: ManualDraft, spans: PiiSpan[]): boolean =>
  spans.some((s) => draft.start < s.end && s.start < draft.end);

const PiiReviewDialog = ({
  open,
  originalText,
  spans,
  onCancel,
  onConfirm,
}: PiiReviewDialogProps) => {
  const [manualSpans, setManualSpans] = useState<PiiSpan[]>([]);
  const [decisions, setDecisions] = useState<Record<string, SpanDecision>>({});
  const [showAnonymised, setShowAnonymised] = useState(false);
  const [manualDraft, setManualDraft] = useState<ManualDraft | null>(null);
  const [manualType, setManualType] = useState<PiiType>("name");
  const [manualLabel, setManualLabel] = useState("");
  const previewRef = useRef<HTMLParagraphElement>(null);

  // Reset everything whenever a new scan arrives.
  useEffect(() => {
    setManualSpans([]);
    setDecisions({});
    setShowAnonymised(false);
    setManualDraft(null);
  }, [spans]);

  // Detected + manual spans, re-numbered together so manual additions get an
  // entityId and coreference/numbering stays consistent across the whole prompt.
  const effectiveSpans = useMemo(
    () => numberSpans([...spans, ...manualSpans]),
    [spans, manualSpans]
  );

  const decisionFor = (key: string): SpanDecision => decisions[key] ?? "redact";

  const groupKeys = useMemo(() => {
    const seen = new Set<string>();
    for (const s of effectiveSpans) seen.add(groupKeyOf(s));
    return [...seen];
  }, [effectiveSpans]);

  const presentTypes = useMemo(() => {
    const seen = new Set<PiiType>();
    for (const s of effectiveSpans) seen.add(s.type);
    return [...seen];
  }, [effectiveSpans]);

  const keptCount = groupKeys.filter((k) => decisionFor(k) === "keep").length;

  // Plain toggle for every tier — hard overrides are still audited server-side;
  // the extra in-dialog confirm was dropped after team testing (tenant training
  // owns that behaviour).
  const toggleGroup = (key: string) =>
    setDecisions((prev) => ({
      ...prev,
      [key]: decisionFor(key) === "redact" ? "keep" : "redact",
    }));

  const handleConfirm = () => {
    const decided: DecidedSpan[] = effectiveSpans.map((span) => ({
      ...span,
      decision: decisionFor(groupKeyOf(span)),
    }));
    onConfirm(decided);
  };

  // Works in both views — offsets are per-segment anchored, so the anonymised
  // rendering can't skew them (see offsetOf).
  const handlePreviewMouseUp = () => {
    if (!previewRef.current) return;
    const draft = selectionToRange(previewRef.current, originalText);
    if (draft && !overlapsExisting(draft, effectiveSpans)) {
      setManualType("name");
      setManualLabel("");
      setManualDraft(draft);
    }
  };

  const addManual = () => {
    if (!manualDraft) return;
    const label =
      manualType === "custom" ? manualLabel.trim() || undefined : undefined;
    setManualSpans((prev) => [
      ...prev,
      {
        start: manualDraft.start,
        end: manualDraft.end,
        text: manualDraft.text,
        type: manualType,
        tier: PII_TIERS[manualType],
        source: "manual",
        confidence: 1,
        ...(label ? { label } : {}),
      },
    ]);
    setManualDraft(null);
    window.getSelection()?.removeAllRanges();
  };

  // Split the prompt into plain + highlight parts for the preview. `start` is
  // the part's offset in the ORIGINAL text — plain parts expose it in the DOM
  // (data-pii-offset) as the anchor for selection→offset mapping.
  const parts = useMemo(() => {
    const out: Array<{
      text: string;
      start: number;
      spanIndex: number | null;
    }> = [];
    let cursor = 0;
    effectiveSpans.forEach((span, i) => {
      if (span.start > cursor) {
        out.push({
          text: originalText.slice(cursor, span.start),
          start: cursor,
          spanIndex: null,
        });
      }
      out.push({
        text: originalText.slice(span.start, span.end),
        start: span.start,
        spanIndex: i,
      });
      cursor = Math.max(cursor, span.end);
    });
    if (cursor < originalText.length) {
      out.push({
        text: originalText.slice(cursor),
        start: cursor,
        spanIndex: null,
      });
    }
    return out;
  }, [originalText, effectiveSpans]);

  const displayFor = (span: PiiSpan): string =>
    showAnonymised && decisionFor(groupKeyOf(span)) === "redact"
      ? `${placeholderFor(span.type, span.entityId, span.label)}${span.inflection ?? ""}`
      : span.text;

  const renderHighlight = (span: PiiSpan, key: number) => {
    const gk = groupKeyOf(span);
    const anonymise = decisionFor(gk) === "redact";
    return (
      <button
        key={key}
        type="button"
        onClick={() => toggleGroup(gk)}
        title={`${PII_TYPE_LABELS[span.type]} — ${
          anonymise ? TEXT.willAnonymise : TEXT.willKeep
        }`}
        aria-pressed={anonymise}
        className={`relative mx-px inline-flex cursor-pointer items-center rounded-sm border-b-2 px-0.5 align-baseline text-foreground ${
          PII_TYPE_BADGE_CLASS[span.type]
        } ${anonymise ? "" : "opacity-80"}`}
      >
        <span>{displayFor(span)}</span>
        {/* data-pii-ignore: decoration text — excluded from selection offsets */}
        {span.entityId !== undefined && (
          <span
            data-pii-ignore
            className="ml-0.5 select-none align-super text-[0.65em] font-semibold text-muted-foreground"
          >
            {span.entityId}
          </span>
        )}
        <span
          data-pii-ignore
          className="absolute -right-1.5 -top-1.5 flex h-3.5 w-3.5 select-none items-center justify-center rounded-full bg-background ring-1 ring-border"
        >
          {anonymise ? (
            <Check className="h-2.5 w-2.5 text-success" />
          ) : (
            <X className="h-2.5 w-2.5 text-destructive" />
          )}
        </span>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <ShieldAlert className="h-5 w-5 text-warning" />
            {TEXT.title}
          </DialogTitle>
          <DialogDescription>{TEXT.description}</DialogDescription>
        </DialogHeader>

        {/* Preview — the control surface */}
        <div className="overflow-hidden rounded-md border bg-muted/30">
          <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-1.5">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={showAnonymised}
                onCheckedChange={(c) => setShowAnonymised(c === true)}
              />
              {TEXT.showAnonymised}
            </label>
            <span className="text-xs text-muted-foreground">
              {TEXT.selectHint}
            </span>
          </div>
          {/* Native overflow (not ScrollArea): long prompts MUST scroll. */}
          <div className="max-h-[45vh] overflow-y-auto">
            <p
              ref={previewRef}
              onMouseUp={handlePreviewMouseUp}
              className="whitespace-pre-wrap px-3 py-4 text-sm leading-7 text-foreground"
            >
              {parts.map((part, i) =>
                part.spanIndex === null ? (
                  <span key={i} data-pii-offset={part.start}>
                    {part.text}
                  </span>
                ) : (
                  renderHighlight(effectiveSpans[part.spanIndex], i)
                )
              )}
            </p>
          </div>
        </div>

        {/* Manual classify panel (appears when the user selects text) */}
        {manualDraft && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed bg-card p-2">
            <span className="text-xs text-muted-foreground">
              {TEXT.markAs(manualDraft.text)}
            </span>
            <ToggleGroup
              type="single"
              size="sm"
              value={manualType}
              onValueChange={(v) => v && setManualType(v as PiiType)}
            >
              {MANUAL_TYPES.map((t) => (
                <ToggleGroupItem key={t.value} value={t.value}>
                  {t.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {manualType === "custom" && (
              // The label is committed by "Tilføj" right next to it; the review
              // dialog itself is the Info tier (LAR-214).
              // eslint-disable-next-line local/no-input-outside-form-dialog
              <Input
                value={manualLabel}
                onChange={(e) => setManualLabel(e.target.value)}
                placeholder={TEXT.otherLabel}
                className="h-8 w-40"
              />
            )}
            <Button size="sm" onClick={addManual}>
              {TEXT.add}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setManualDraft(null)}
            >
              {TEXT.cancelManual}
            </Button>
          </div>
        )}

        {/* Legend: decision markers + present types */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Check className="h-3 w-3 text-success" />
            {TEXT.willAnonymise}
          </span>
          <span className="flex items-center gap-1">
            <X className="h-3 w-3 text-destructive" />
            {TEXT.willKeep}
          </span>
          {presentTypes.map((type) => (
            <span key={type} className="flex items-center gap-1.5">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${PII_TYPE_DOT_CLASS[type]}`}
              />
              {PII_TYPE_LABELS[type]}
            </span>
          ))}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {TEXT.summary(groupKeys.length, keptCount)}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              {TEXT.cancel}
            </Button>
            <Button onClick={handleConfirm}>{TEXT.send}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PiiReviewDialog;
